import React from 'react';
import {
  ArrowRightIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  CpuChipIcon,
  GlobeAltIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import logoUrl from './new2.png';

const App: React.FC = () => {
  return (
    <div className="bg-slate-950 text-white">
      <div className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-aurora opacity-70" />
        <div className="absolute inset-0 bg-grid opacity-40" />
        <header className="relative z-10">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-8 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center justify-center md:justify-start">
              <img
                src={logoUrl}
                alt="Alt Infotech Ltd logo"
                className="h-20 w-auto sm:h-24 md:h-28"
              />
            </div>
            <nav className="hidden items-center gap-8 text-sm text-slate-200 md:flex">
              <a href="#about" className="hover:text-white">About</a>
              <a href="#services" className="hover:text-white">Services</a>
              <a href="#solutions" className="hover:text-white">Solutions</a>
              <a href="#technologies" className="hover:text-white">Technologies</a>
              <a href="#contact" className="hover:text-white">Contact</a>
            </nav>
            <a
              href="#contact"
              className="hidden rounded-full border border-white/20 px-5 py-2 text-sm text-white transition hover:border-white/60 md:inline-flex"
            >
              Start a project
            </a>
          </div>
        </header>

        <section className="relative z-10">
          <div className="mx-auto grid max-w-6xl gap-12 px-6 pb-24 pt-10 md:grid-cols-[1.2fr_0.8fr] md:items-center">
            <div className="animate-rise">
              <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-200">
                AI-first software development company
              </p>
              <h1 className="font-display text-4xl font-semibold leading-tight md:text-6xl">
                Build SaaS and cloud platforms that feel intelligent, fast, and reliable.
              </h1>
              <p className="mt-6 text-lg text-slate-200">
                Alt Infotech Ltd is a UK-based software development company specialising in
                SaaS products, CRM systems, data platforms, and AI-driven automation. We blend
                deep engineering with strategic consulting to unlock new possibilities for our clients.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="#solutions"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3 text-slate-900 transition hover:-translate-y-0.5"
                >
                  Explore solutions <ArrowRightIcon className="h-5 w-5" />
                </a>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/30 px-6 py-3 text-white transition hover:border-white"
                >
                  Book a discovery call
                </a>
              </div>
              <div className="mt-10 grid gap-4 text-sm text-slate-200 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="font-semibold text-white">SaaS + CRM</p>
                  <p className="text-slate-300">Multi-tenant platforms across travel, health, finance.</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="font-semibold text-white">AI automation</p>
                  <p className="text-slate-300">LLM copilots, RAG search, workflow orchestration.</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <p className="font-semibold text-white">Cloud native</p>
                  <p className="text-slate-300">Secure, scalable infrastructure on AWS, Azure, GCP.</p>
                </div>
              </div>
            </div>

            <div className="relative animate-rise animate-delay-300">
              <div className="absolute -top-6 left-8 h-40 w-40 rounded-full bg-emerald-400/20 blur-3xl" />
              <div className="absolute -bottom-10 right-0 h-52 w-52 rounded-full bg-blue-500/20 blur-3xl" />
              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-200">Quick links</p>
                  <ul className="mt-5 space-y-4 text-sm">
                    <li>
                      <a href="#services" className="flex items-center justify-between text-white hover:text-slate-100">
                        Services overview <ArrowRightIcon className="h-4 w-4" />
                      </a>
                    </li>
                    <li>
                      <a href="#technologies" className="flex items-center justify-between text-white hover:text-slate-100">
                        Technology stack <ArrowRightIcon className="h-4 w-4" />
                      </a>
                    </li>
                    <li>
                      <a href="#contact" className="flex items-center justify-between text-white hover:text-slate-100">
                        Contact and location <ArrowRightIcon className="h-4 w-4" />
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="mt-6 grid gap-4 text-sm text-slate-200">
                  <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <SparklesIcon className="h-5 w-5 text-emerald-300" />
                    <div>
                      <p className="font-semibold text-white">AI product thinking</p>
                      <p className="text-slate-300">From opportunity mapping to production-ready AI features.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-300" />
                    <div>
                      <p className="font-semibold text-white">Governance ready</p>
                      <p className="text-slate-300">Security, compliance, and observability from day one.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section id="about" className="bg-white text-slate-900">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-20 md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-700">About us</p>
            <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">
              A software development partner with a lateral mindset.
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              We deliver SaaS applications, business intelligence, and AI automation that help teams
              operate with clarity and speed. Our engineers and consultants combine domain expertise,
              hands-on delivery, and modern cloud practices to build solutions that stay reliable as you scale.
            </p>
          </div>
          <div className="grid gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <p className="font-semibold">What we believe</p>
              <p className="mt-2 text-sm text-slate-600">
                Practical innovation, measurable outcomes, and clean execution.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <p className="font-semibold">How we work</p>
              <p className="mt-2 text-sm text-slate-600">
                Agile delivery, transparent collaboration, and quality-first engineering.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="bg-slate-950">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-slate-200">Services</p>
              <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">
                Engineering depth with strategic direction.
              </h2>
            </div>
            <a href="#contact" className="text-sm text-slate-300 hover:text-white">
              Plan your engagement
            </a>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-7 transition hover:-translate-y-1">
              <CpuChipIcon className="h-7 w-7 text-emerald-300" />
              <h3 className="mt-4 text-xl font-semibold text-white">AI-powered software development</h3>
              <p className="mt-3 text-sm text-slate-300">
                Custom web and mobile applications with LLM copilots, RAG search,
                automation agents, and secure integrations.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-7 transition hover:-translate-y-1">
              <CloudArrowUpIcon className="h-7 w-7 text-blue-300" />
              <h3 className="mt-4 text-xl font-semibold text-white">Cloud-native SaaS platforms</h3>
              <p className="mt-3 text-sm text-slate-300">
                Multi-tenant SaaS in CRM, travel, health, and finance with
                secure data models and real-time analytics.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-7 transition hover:-translate-y-1">
              <ChartBarIcon className="h-7 w-7 text-amber-200" />
              <h3 className="mt-4 text-xl font-semibold text-white">Data, BI, and reporting</h3>
              <p className="mt-3 text-sm text-slate-300">
                Data warehouses, dashboards, and predictive insights that make
                decisions fast and trusted.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-7 transition hover:-translate-y-1">
              <LightBulbIcon className="h-7 w-7 text-rose-200" />
              <h3 className="mt-4 text-xl font-semibold text-white">Management and tech consulting</h3>
              <p className="mt-3 text-sm text-slate-300">
                Strategy, market intelligence, and delivery roadmaps aligned with
                your vision and operating model.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="solutions" className="bg-white text-slate-900">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="grid gap-8 md:grid-cols-[1fr_1.2fr] md:items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-slate-700">Solutions</p>
              <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">
                Delivering impact across the full product lifecycle.
              </h2>
              <p className="mt-6 text-lg text-slate-600">
                We handle discovery, design, development, and support. Our teams build
                modern SaaS products with measurable business outcomes and AI features
                that are transparent, safe, and scalable.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                {
                  title: 'SaaS product engineering',
                  desc: 'From MVP to enterprise-grade platforms with reusable accelerators.',
                },
                {
                  title: 'CRM and workflow systems',
                  desc: 'Process automation, customer intelligence, and system integrations.',
                },
                {
                  title: 'AI copilots and assistants',
                  desc: 'Context-aware experiences with guardrails and observability.',
                },
                {
                  title: 'Cloud modernization',
                  desc: 'Microservices, event-driven architecture, and platform reliability.',
                },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="font-semibold">{item.title}</p>
                  <p className="mt-2 text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="technologies" className="bg-slate-950">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="grid gap-10 md:grid-cols-[0.9fr_1.1fr] md:items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-slate-200">Technologies</p>
              <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">
                Modern stacks, proven enterprise tooling.
              </h2>
              <p className="mt-6 text-lg text-slate-300">
                We work across Java, .NET, and modern JavaScript frameworks while supporting
                data platforms like Postgres, SQL Server, and Oracle. We also build with
                cloud-native services, event-driven systems, and AI tooling.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                'React, Next.js, TypeScript, Node.js',
                'Java, J2EE, .NET, Android',
                'Postgres, SQL Server, Oracle, MS SQL',
                'Microservices, ESB, event-driven systems',
                'AWS, Azure, GCP, Docker, Kubernetes',
                'LLMs, vector search, RAG pipelines',
                'IoT applications and streaming data',
                'BI tools: Informatica, Business Objects, QlikView',
              ].map((tech) => (
                <div key={tech} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                  {tech}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white text-slate-900">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-20 md:grid-cols-[1fr_1.1fr] md:items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-700">Industries</p>
            <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">
              Domain expertise that accelerates delivery.
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              We have delivered SaaS and data platforms for CRM, travel, health, and finance,
              with deep attention to compliance, user workflows, and secure data handling.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { title: 'CRM & customer experience', icon: GlobeAltIcon },
              { title: 'Travel & logistics', icon: CloudArrowUpIcon },
              { title: 'Health & wellbeing', icon: ShieldCheckIcon },
              { title: 'Finance & payments', icon: ChartBarIcon },
            ].map((item) => (
              <div key={item.title} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <item.icon className="h-5 w-5 text-slate-700" />
                <span className="text-sm font-semibold">{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="bg-slate-950">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-slate-200">Contact</p>
              <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">
                Let&apos;s build something that matters.
              </h2>
              <p className="mt-6 text-lg text-slate-300">
                Tell us about your product goals or transformation roadmap. We will respond with a
                practical plan and the right experts to move fast.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="mailto:info@altinfotech.com"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3 text-slate-900 transition hover:-translate-y-0.5"
                >
                  Email us <ArrowRightIcon className="h-5 w-5" />
                </a>
                <a
                  href="https://www.altinfotech.com"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/30 px-6 py-3 text-white transition hover:border-white"
                >
                  Visit altinfotech.com
                </a>
              </div>
            </div>
            <div className="grid gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-200">Office</p>
                <p className="mt-3 text-white">
                  Alt Infotech Ltd<br />
                  154 Pears Road<br />
                  London TW3 1SJ<br />
                  United Kingdom
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-200">Company</p>
                <p className="mt-3 text-slate-200">
                  Registration Number: 6741020<br />
                  VAT Number: 941201761<br />
                  Email: info@altinfotech.com
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-200">Engagement</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-200">
                  <li>Discovery and product strategy</li>
                  <li>Design and engineering delivery</li>
                  <li>Cloud migration and managed support</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-slate-400 md:flex-row">
            <p>Alt Infotech Ltd. All rights reserved.</p>
            <p>Let&apos;s think laterally!</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default App;
